/* eslint-disable no-restricted-syntax */
/* eslint-disable no-prototype-builtins */
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { catchError, tap } from 'rxjs/operators';
import { of, Observable, Subscription } from 'rxjs';
import { Network } from '@capacitor/network';

import { DeliveryAsset, OfflineDownloadStatus } from '../models';
import { OfflineService } from './offline.service';
import * as fromOffline from '../store/actions/offline.actions';
import * as fromOfflineStatusesActions from '../store/actions/offline-statuses.actions';
import { downloadNext } from '../store/actions/offline-statuses.actions';
import { ToastService } from './toast.service';

interface Downloadings {
  deliveryKeyCode: { subscription: Subscription; asset?: DeliveryAsset }[];
}

@Injectable({ providedIn: 'root' })
export class OfflineDownloadingService {
  private downloads: Downloadings = <Downloadings>{};
  private isOnline = true;

  constructor(
    private store: Store<any>,
    private offlineService: OfflineService,
    private toastService: ToastService,
  ) {
    Network.addListener('networkStatusChange', ({ connected }) => {
      this.isOnline = connected;
    });
  }

  downloadAudioFile(asset: DeliveryAsset, isAllowToDownload: boolean): void {
    const existDownloading = this.getIsDownloadingExist(asset);

    if (!existDownloading && isAllowToDownload) {
      this.startDownloadingAudioFile(asset);
    } else {
      this.showDownloadingProblem(existDownloading, isAllowToDownload);
    }
  }

  dispatchEventStatus(asset: DeliveryAsset, eventStatus: OfflineDownloadStatus): void {
    this.store.dispatch(fromOfflineStatusesActions.sendOfflineDwnlStatus({ asset, eventStatus }));
  }

  clearDownlaodingList(deliveryKeyCode, finished = false): void {
    const downloads = this.downloads[deliveryKeyCode];
    if (downloads) {
      downloads.forEach((download: any) => {
        download.subscription.unsubscribe();
        if (!finished) {
          this.dispatchEventStatus(download.asset, 'CANCELLED');
        }
      });
      delete this.downloads[deliveryKeyCode];
      this.setOfflineDownloadingState(false);
    }
  }

  clearDownlaodingAllList(): void {
    for (const key in this.downloads) {
      if (this.downloads.hasOwnProperty(key)) {
        const downloads = this.downloads[key];
        downloads.forEach((download: any) => {
          download.subscription.unsubscribe();
          this.dispatchEventStatus(download.asset, 'CANCELLED');
        });
      }
    }
    this.setOfflineDownloadingState(false);
    this.downloads = <Downloadings>{};
  }

  private startDownloadingAudioFile(asset: DeliveryAsset): void {
    this.setOfflineDownloadingState(true);
    this.changeAssetOfflineStatus(asset, 'DOWNLOADING');
    this.dispatchEventStatus(asset, 'STARTED');
    this.saveDownloadSubsciption(asset, this.getDownloadAudioFileSubscription(asset));
  }

  private getDownloadAudioFileSubscription(asset: DeliveryAsset): Subscription {
    return this.offlineService
      .downloadAudioFile(asset)
      .pipe(
        tap((file: Blob) => {
          this.removeFromDownloadingList(asset);
          this.downloadAudioFileSuccess(file, asset);
        }),
        catchError((error: any) => this.cachedDownloadError(error, asset)),
      )
      .subscribe();
  }

  private downloadAudioFileFail(asset: DeliveryAsset, error): Observable<any> {
    this.toastService.showErrorInToast(
      'offline-downlaoding.service',
      'downlaodAudioFileFail',
      error,
    );

    const status = 'DAMAGED';
    this.changeAssetOfflineStatus(asset, status);
    if (navigator.onLine) {
      this.toastService.showToast(`Download asset failed, - ${asset.trackTitle}`);
    }

    return of();
  }

  private downloadAudioFileSuccess(file: Blob, asset: DeliveryAsset): void {
    this.setOfflineDownloadingState(false);
    if (file) {
      this.dispatchEventStatus(asset, 'COMPLETED');
      this.store.dispatch(
        downloadNext({ deliveryKeyCode: asset.deliveryKeyCode, eventStatus: 'COMPLETED' }),
      );
      this.store.dispatch(fromOffline.writeToAudioFile({ asset, file }));
    } else {
      this.store.dispatch(
        downloadNext({ deliveryKeyCode: asset.deliveryKeyCode, eventStatus: 'DAMAGED' }),
      );
    }
  }

  private changeAssetOfflineStatus(asset, status: OfflineDownloadStatus): void {
    const modifiedAsset = { ...asset };

    modifiedAsset.offlineDownloadStatus = status;
    this.store.dispatch(fromOffline.updateOfflineAsset({ asset: modifiedAsset }));
  }

  private getIsDownloadingExist(asset: DeliveryAsset): boolean {
    let isDownloadingExist = false;
    const currentDownloads = this.downloads[asset.deliveryKeyCode];
    if (currentDownloads) {
      currentDownloads.forEach((download: any) => {
        if (download.asset.campaignTrackId === asset.campaignTrackId) {
          isDownloadingExist = true;
        }
      });
    }
    return isDownloadingExist;
  }

  private removeFromDownloadingList(asset: DeliveryAsset): void {
    const currentDownloads = this.downloads[asset.deliveryKeyCode];

    if (currentDownloads) {
      const findIndex = currentDownloads.findIndex((download: any) => (
        download.asset.campaignTrackId === asset.campaignTrackId
      ));

      if (findIndex !== -1) {
        currentDownloads.splice(findIndex, 1);
      }
    }
  }

  private cachedDownloadError(error, asset): Observable<any> {
    this.setOfflineDownloadingState(false);
    this.removeFromDownloadingList(asset);
    this.downloadAudioFileFail(asset, error);
    if (this.isOnline) {
      this.store.dispatch(
        downloadNext({ deliveryKeyCode: asset.deliveryKeyCode, eventStatus: 'DAMAGED' }),
      );
    }
    return of(error);
  }
  private showDownloadingProblem(existDownloading, isAllowToDownload): void {
    if (existDownloading) {
      this.toastService.showErrorInToast(
        'offline-downloading',
        'download-audio-file',
        'downloadingInProgress',
      );
    }
    if (!isAllowToDownload) {
      this.toastService.showErrorInToast(
        'offline-downloading',
        'download-audio-file',
        'downloadingPermissionDenied',
      );
    }
  }

  private saveDownloadSubsciption(asset, subscription): void {
    if (!this.downloads[asset.deliveryKeyCode]) {
      this.downloads[asset.deliveryKeyCode] = [];
    }
    this.downloads[asset.deliveryKeyCode].push({ asset, subscription });
  }

  private setOfflineDownloadingState(isDownloading): void {
    this.store.dispatch(fromOffline.updateOfflineDownloadingState({ isDownloading }));
  }
}
